import useMakeTransfer from 'hooks/Bank/useMakeTransfer';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { TransferFormStepProps, TransferReviewData } from '../types';
import { transformFormDataToReviewData } from '../utils/dataTransforms';
import ExternalTransfer, {
  type InitialPaymentData,
} from './ExternalTransfer.component';
import InternalTransferComponent from './InternalTransfer.component';

const TransferFormStep: FC<TransferFormStepProps> = ({
  sourceAccount,
  receiptAccount,
  initialAmount,
  isInternalTransfer,
  onTransferSuccess,
  isStandingOrderPayment,
  onReview,
  preservedFormData,
}) => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useMakeTransfer({
    withRedirection: true,
    onTransferSuccess,
    redirectionParams: {
      successfulPayment: 'true',
      isScheduledPayment: isStandingOrderPayment ? 'true' : undefined,
    },
  });

  // For internal transfers, handle them with review step
  if (isInternalTransfer && receiptAccount !== 'external') {
    const handleInternalReview = (reviewData: TransferReviewData) => {
      // Pass review data to the parent component
      onReview?.(reviewData);
    };

    return (
      <InternalTransferComponent
        sourceAccount={sourceAccount}
        destinationAccount={receiptAccount}
        initialAmount={initialAmount}
        onTransferSuccess={onTransferSuccess}
        onReview={handleInternalReview}
        showReviewStep={true}
      />
    );
  }

  // Handle external transfer form submission
  const handleExternalTransferSubmit = (formData: Record<string, any>) => {
    // Prepare review data for external transfers using the transformer
    const reviewData = transformFormDataToReviewData(
      formData,
      mutate,
      isLoading,
      sourceAccount?._id,
    );
    onReview?.(reviewData);
  };

  // Use preserved form data if available, otherwise use initial amount
  const initialData = preservedFormData || { amount: initialAmount };

  return (
    <div className="w-full px-6">
      <ExternalTransfer
        initialData={initialData as InitialPaymentData}
        onSubmit={handleExternalTransferSubmit}
        sourceAccountBalance={sourceAccount?.balance?.amount}
        isStandingOrderPayment={isStandingOrderPayment}
        btnLabel={t('accountable_banking.review_transfer.title')}
        sourceAccount={sourceAccount}
        formType="external"
      />
    </div>
  );
};

export default TransferFormStep;
